<template>
  <CCard class="setting-card admin-mfa-card">
    <CCardHeader>
      <div class="admin-mfa-heading">
        <div class="section-heading">
          <span class="section-icon"><CIcon name="cil-shield-alt"/></span>
          <div>
            <strong>管理者の2要素認証</strong>
            <p>管理者権限の方に、認証アプリの6桁を必須にできます。</p>
            <!--
              **「保存済み」だけでは答えになっていない。**
              必須なのか、いつからなのか、対象が何人なのかが
              開かないと分からない状態にしない。
            -->
            <ul v-if="compact && !loading" class="setting-values">
              <li
                class="setting-values__chip"
                :class="{ 'setting-values__chip--on': required }"
              >{{ required ? '必須' : '任意' }}</li>
              <li v-if="required && requiredFrom" class="setting-values__chip">
                {{ requiredFrom }} から
              </li>
              <li class="setting-values__chip">
                対象 {{ form.targetAdmins }}名
              </li>
              <li
                v-if="form.targetAdminsNotEnrolled > 0"
                class="setting-values__chip setting-values__chip--warn"
              >未登録 {{ form.targetAdminsNotEnrolled }}名</li>
            </ul>
          </div>
        </div>
        <CBadge :color="dirty ? 'warning' : 'success'">
          {{ dirty ? '未保存の変更あり' : '保存済み' }}
        </CBadge>
      <SettingSectionAction
        v-if="compact"
        :to="{ name: '自社設定の管理者2要素認証' }"
      />
      </div>
    </CCardHeader>

    <CCardBody v-if="!compact">
      <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>
      <CAlert v-if="message" color="success">{{ message }}</CAlert>

      <div v-if="loading" class="admin-mfa-loading">
        <CSpinner color="primary" size="sm"/> 読み込んでいます。
      </div>

      <template v-else>
        <!--
          鍵が無いのに必須にすると、その会社の管理者は全員ログインできなく
          なる（サーバが 503 を返し、登録画面にも設定画面にも入れない）。

          **押せる形で出さない。**サーバも同じ条件で断るが、押してから
          断られるのでは「なぜ押せたのか」が残る。
        -->
        <CAlert v-if="!form.mfaSecretKeyConfigured" color="warning">
          <b>いまは必須にできません。</b>
          サーバ側の2要素認証の準備が完了していません。
          PREIKAサポートへお問い合わせください。
        </CAlert>

        <div class="admin-mfa-switch">
          <label class="admin-mfa-switch__label">
            <input
              id="adminMfaRequired"
              type="checkbox"
              v-model="required"
              :disabled="saving || !canTurnOn"
            >
            <span>管理者に2要素認証を必須にする</span>
          </label>
          <p class="admin-mfa-switch__note">
            対象は<b>管理者権限の方</b>です。一般の方には影響しません。
          </p>
        </div>

        <template v-if="required">
          <div class="admin-mfa-field">
            <label for="adminMfaRequiredFrom">
              必須にする日<span class="asterisk">*</span>
            </label>
            <input
              id="adminMfaRequiredFrom"
              type="date"
              class="form-control"
              v-model="requiredFrom"
              :min="form.earliestRequiredFrom"
              :disabled="saving"
            >
            <small>
              <b>{{ form.earliestRequiredFrom }} 以降</b>にしてください。
              直前に切り替えると、まだ登録していない管理者が
              その日から仕事にならなくなります。
            </small>
          </div>

          <div class="admin-mfa-field">
            <label for="adminMfaPromptLead">登録を促す期間</label>
            <select
              id="adminMfaPromptLead"
              class="form-control"
              v-model="promptLead"
              :disabled="saving"
            >
              <option :value="30">必須の30日前から促す</option>
              <option :value="14">必須の14日前から促す</option>
              <option :value="null">促さない（案内だけ）</option>
            </select>
            <small>
              促す期間に入ると、ログインのたびに登録画面が出ます。
              「あとで」は<b>3回まで</b>押せます。
            </small>
          </div>

          <!--
            日付を4つ並べる代わりに、導かれた結果を1文で読み上げる。
            responsibility は責任者にあるので、**何がいつ起きるか**が
            読んで分かる形にする。
          -->
          <CAlert v-if="schedule" color="info" class="admin-mfa-schedule">
            {{ schedule }}
          </CAlert>
        </template>

        <dl class="admin-mfa-counts">
          <div>
            <dt>対象の管理者</dt>
            <dd>{{ form.targetAdmins }}人</dd>
          </div>
          <div>
            <dt>うち、まだ登録していない方</dt>
            <dd :class="{ 'admin-mfa-counts__warn': form.targetAdminsNotEnrolled > 0 }">
              {{ form.targetAdminsNotEnrolled }}人
            </dd>
          </div>
          <div>
            <dt>設定を解除できる方（契約企業オーナー）</dt>
            <dd :class="{ 'admin-mfa-counts__warn': form.tenantOwners < 2 }">
              {{ form.tenantOwners }}人
            </dd>
          </div>
        </dl>

        <!--
          対象は authority が「管理者」の人だけ。権限管理から
          TENANT_OWNER などを付けられた人は**強い権限を持ったまま対象外**
          で残る。「管理者に必須にしました」と言い切ると嘘になる。
        -->
        <CAlert
          v-if="form.privilegedButNotTargeted > 0"
          color="warning"
        >
          この設定の対象ではないものの、<b>利用者や会社設定を変更できる
          方が{{ form.privilegedButNotTargeted }}人</b>います。
          その方々には2要素認証は必須になりません。
        </CAlert>

        <CAlert v-if="form.tenantOwners < 2" color="warning">
          契約企業オーナーが{{ form.tenantOwners }}人のため、必須にできません。
          その方が認証アプリと予備コードを両方失うと、<b>社内の誰も解除
          できなくなります。</b>先にオーナーをもう1人決めてください。
        </CAlert>

        <div class="admin-mfa-notes">
          <strong>保存する前に</strong>
          <ul>
            <li>
              この設定は、パスワード・パスキー・Google のどの入口から入った
              管理者にも同じように効きます。
            </li>
            <li>
              保存しても、いまログイン中の管理者はそのままです。
              <b>次のログインから</b>対象になります。
            </li>
            <li>
              認証アプリを失った方は、予備コードか、契約企業オーナーによる
              解除で戻れます。
            </li>
          </ul>
        </div>

        <CAlert v-if="formError" color="danger">{{ formError }}</CAlert>

        <div class="admin-mfa-actions">
          <CButton
            color="primary"
            :disabled="saving || !dirty || blocked"
            @click="save"
          >{{ saving ? '保存しています' : '保存する' }}</CButton>
          <CButton
            color="link"
            :disabled="saving || !dirty"
            @click="reset"
          >変更を取り消す</CButton>
        </div>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import SettingSectionAction from './SettingSectionAction.vue'
import api from '@/services/adminMfaPolicySettings'

/**
 * 管理者の2要素認証を、契約企業の責任者が設定する画面。
 *
 * ## 決めるのは3つだけ
 *
 * DBには日付が3つと回数が1つあるが、**意味が逆に読める。**入力欄を並べる
 * と読み違いがそのまま設定になるので、必須の日と促す期間だけを聞いて、
 * 残りはサーバが導く。導かれた結果は文で読み上げる。
 *
 * ## ここの縛りは守りではない
 *
 * 鍵の有無・日の近さ・オーナーの数は、**すべてサーバが断る。**ここで
 * 押せなくしているのは「押す前に気づける」ため。画面を通さずに投げられ
 * たら意味が無いので、これを守りに数えない。
 */
export default {
  name: 'AdminMfaPolicySettings',

  components: { SettingSectionAction },

  /**
   * 自社設定の一覧では **たたんで出す。**
   *
   * たたまないころは、この部品だけで縦 577〜1,389px を使っていて、
   * その中に自前の保存ボタンがあった。一覧に保存ボタンが散らばると、
   * どこを押せば何が決まるのか画面ごとに探すことになる。
   *
   * たたんだときは見出しと「変更」だけを出し、**中身も保存ボタンも
   * 出さない。**直すのは専用の画面で、決定はそこに1つだけ置く。
   */
  props: {
    compact: { type: Boolean, default: false }
  },

  data () {
    return {
      loading: true,
      saving: false,
      form: {
        mfaSecretKeyConfigured: false,
        earliestRequiredFrom: '',
        targetAdmins: 0,
        targetAdminsNotEnrolled: 0,
        privilegedButNotTargeted: 0,
        tenantOwners: 0
      },
      required: false,
      requiredFrom: '',
      promptLead: 14,
      /** 読み込んだ直後の値。「変えたか」を見るために持つ。 */
      loaded: { required: false, requiredFrom: '', promptLead: 14 },
      errorMessage: '',
      formError: '',
      message: ''
    }
  },

  computed: {
    /**
     * 鍵が無い間はONにできない。
     *
     * **既にONの会社は、OFFへ動かせる。**動かせないと、鍵を失った会社が
     * 解除もできなくなる。
     */
    canTurnOn () {
      return this.form.mfaSecretKeyConfigured || this.loaded.required
    },

    dirty () {
      return this.required !== this.loaded.required
        || (this.required && this.requiredFrom !== this.loaded.requiredFrom)
        || (this.required && this.promptLead !== this.loaded.promptLead)
    },

    /** 押しても通らないと分かっている状態。 */
    blocked () {
      if (!this.required) return false
      if (!this.form.mfaSecretKeyConfigured) return true
      if (!this.requiredFrom) return true
      if (this.form.earliestRequiredFrom
        && this.requiredFrom < this.form.earliestRequiredFrom) return true
      return this.form.tenantOwners < 2
    },

    /** 何がいつ起きるかを、1文で。 */
    schedule () {
      if (!this.required || !this.requiredFrom) return ''
      const parts = ['今日から案内が出ます']
      if (this.promptLead) {
        parts.push('必須の' + this.promptLead
          + '日前から、ログインのたびに登録画面が出ます（あとでは3回まで）')
      }
      parts.push(this.requiredFrom + ' から、登録しないとログインできません')
      return parts.join('。') + '。'
    }
  },

  mounted () {
    this.reload()
  },

  methods: {
    async reload () {
      this.loading = true
      this.errorMessage = ''
      try {
        this.apply(await api.get())
      } catch (error) {
        this.errorMessage = this.explain(
          error, '2要素認証の設定を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    apply (data) {
      const one = data || {}
      this.form = {
        mfaSecretKeyConfigured: one.mfaSecretKeyConfigured === true,
        earliestRequiredFrom: one.earliestRequiredFrom || '',
        targetAdmins: one.targetAdmins || 0,
        targetAdminsNotEnrolled: one.targetAdminsNotEnrolled || 0,
        privilegedButNotTargeted: one.privilegedButNotTargeted || 0,
        tenantOwners: one.tenantOwners || 0
      }
      this.required = one.mfaRequiredForAdmin === true
      this.requiredFrom = one.mfaRequiredForAdminFrom || ''
      this.promptLead = one.promptLeadDays === undefined
        ? 14 : one.promptLeadDays
      this.loaded = {
        required: this.required,
        requiredFrom: this.requiredFrom,
        promptLead: this.promptLead
      }
    },

    reset () {
      this.required = this.loaded.required
      this.requiredFrom = this.loaded.requiredFrom
      this.promptLead = this.loaded.promptLead
      this.formError = ''
      this.message = ''
    },

    async save () {
      this.formError = ''
      this.message = ''
      this.saving = true
      try {
        /*
         * **送るのは3つだけ。**画面が持っている読み取り専用の値
         * （対象人数、オーナー数、鍵の有無）は送らない。送ると、
         * 画面の値が資格になる形を作ってしまう。
         */
        this.apply(await api.update({
          mfaRequiredForAdmin: this.required,
          mfaRequiredForAdminFrom: this.required ? this.requiredFrom : null,
          promptLeadDays: this.required ? this.promptLead : null
        }))
        this.message = '保存しました。次のログインから反映されます。'
      } catch (error) {
        this.formError = this.explain(
          error, '2要素認証の設定を保存できませんでした。')
      } finally {
        this.saving = false
      }
    },

    /**
     * サーバが返した理由をそのまま見せる。
     *
     * **断る理由はサーバが持っている。**画面で言い換えると、条件を1つ
     * 足したときに古い文言が残る。
     */
    explain (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object' && data.message) return data.message
      if (typeof data === 'string' && data) return data
      return fallback
    }
  }
}
</script>

<style scoped>
/*
  **この部品には .section-heading の指定が無かった。**
  scoped なので、隣のカード（AuthPolicySettings）の定義は届かない。
  そのせいでアイコンが題名の上へ落ち、このカードだけ形が違っていた。
*/
.section-heading { display: flex; align-items: flex-start; gap: 12px; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.section-icon { display: inline-flex; width: 38px; height: 38px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 12px; background: #e9f4fb; color: #0a6ca8; }

/* 一覧に出す「いまの設定」。**状態（保存済み）と値を混ぜない。** */
.setting-values { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 0; padding: 0; list-style: none; }
.setting-values__chip { padding: 2px 10px; border-radius: 999px; background: #eef5fa; color: #28566f; font-size: 12px; line-height: 1.7; }
.setting-values__chip--on { background: #e6f4ea; color: #1d6b3a; font-weight: 700; }
.setting-values__chip--warn { background: #fdf0e6; color: #8a4b20; font-weight: 700; }

.admin-mfa-heading {
  /* 横いっぱいに。伸ばさないと「変更」が右端に来ない。 */
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.admin-mfa-loading {
  color: #6c757d;
}

.admin-mfa-switch {
  margin-bottom: 1.25rem;
}

.admin-mfa-switch__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.admin-mfa-switch__note {
  margin: 0;
  color: #6c757d;
  font-size: 0.875rem;
}

.admin-mfa-field {
  margin-bottom: 1.25rem;
  max-width: 26rem;
}

.admin-mfa-field label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.admin-mfa-field small {
  display: block;
  margin-top: 0.25rem;
  color: #6c757d;
}

.admin-mfa-schedule {
  margin-top: 0.5rem;
}

.admin-mfa-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin: 1.25rem 0;
}

.admin-mfa-counts dt {
  font-size: 0.8125rem;
  color: #6c757d;
  font-weight: 400;
}

.admin-mfa-counts dd {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.admin-mfa-counts__warn {
  color: #e55353;
}

.admin-mfa-notes {
  background: #f8f9fa;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.admin-mfa-notes ul {
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
}

.admin-mfa-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.asterisk {
  color: #e55353;
  margin-left: 0.25rem;
}
</style>
